import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
} from "react-native";
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useIsFocused } from "@react-navigation/native";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import { Observer, observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../../_layout";
import { useTranslation } from "react-i18next";
import FullBlockView from "../../Block/FullBlockView";
import InfoView from "../../Block/InfoView";
import IconEmptyView from "../../Components/IconEmptyView";
import { Entypo, Feather, Ionicons } from "@expo/vector-icons";
import PulseSkeleton from "../../Components/PulseSkeleton";
import ScrollButtons from "../../Components/ScrollButtons";
import ProtocolInfoSection from "../../Block/ProtocolInfoSection";
import Tooltip from "../../Components/Tooltip";

const OpenBlockButton = observer(({ block, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();

  const handleOpenBlock = () => {
    // TODO: Implement navigation to block in Program tab
    block.client.setSelectedBlock(block);
    block.setProperty("isShowingDashboard", true);
    block.client.setProperty("selectedTab", "planification-string");
  };

  return (
    <Tooltip
      content="openBlockInProgram-string"
      placement="top"
      delayedOpen={true}
    >
      <TouchableOpacity
        onPress={handleOpenBlock}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          padding: 1,
          borderRadius: 6,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Feather
          name="arrow-up-right"
          size={16}
          color={isHovered ? Colors.darkgrayColor : Colors.grayColor}
        />
      </TouchableOpacity>
    </Tooltip>
  );
});

const MacroDescriptionView = observer(({ client }) => {
  const { t } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const isFocused = useIsFocused();

  const macro = client.selectedMacro;
  const macroBlocks = client.selectedMacroBlocks;
  const focusedBlock = client.focusedBlock;

  const hasBlocksForSelectedMacro = (macroBlocks?.length || 0) > 0;
  const shouldShowProtocolsGuide = Boolean(
    client?.coach?.showingProtocolsGuide && hasBlocksForSelectedMacro
  );

  // Track which FlatList items are actually visible to clamp offscreen blocks
  const [visibleIds, setVisibleIds] = useState(new Set());

  // Track week visible ranges for each block (for week virtualization)
  const [blockWeekRanges, setBlockWeekRanges] = useState(new Map());

  // Stable fallback object for weekVisibleRange to prevent unnecessary re-renders
  const defaultWeekRange = useMemo(() => ({ start: 0, end: -1 }), []);

  const PopupInfoView = observer(({ block, isVisible, onClose }) => {
    return (
      <Modal
        animationType="fade"
        transparent={true}
        visible={isVisible}
        onRequestClose={onClose}
      >
        <View style={GlobalStyles.centeredView}>
          <TouchableOpacity
            onPress={onClose}
            style={GlobalStyles.touchableBackground}
          >
            <View />
          </TouchableOpacity>
          <View
            style={{
              position: "absolute",
              maxWidth: "90%",
              maxHeight: "90%",
            }}
            onStartShouldSetResponder={() => false}
          >
            <View
              style={[
                styles.modalView,
                {
                  flex: 1,
                },
              ]}
            >
              <ScrollView
                style={{}}
                contentContainerStyle={{}}
                showsVerticalScrollIndicator={false}
              >
                <InfoView
                  client={client}
                  block={block}
                  exercise={block.selectedSlot}
                  calendar={true}
                  coach={null}
                />
              </ScrollView>
            </View>
          </View>
        </View>
      </Modal>
    );
  });

  const BlockOverlay = observer(({ block }) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleGoToProgram = () => {
      block.client.setProperty("selectedTab", "planification-string");
    };

    return (
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 20,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
          borderRadius: 14,
          zIndex: 10,
        }}
        pointerEvents="auto"
      >
        <View
          style={{
            borderWidth: 1,
            alignItems: "center",
            marginTop: 16,
            padding: 24,
            backgroundColor: Colors.whiteColor,
            borderRadius: 14,
            borderColor: Colors.borderColor,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 3,
          }}
          pointerEvents="auto"
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              paddingHorizontal: 8,
              paddingVertical: 3,
              borderRadius: 10,
              maxWidth: "100%",
              backgroundColor: "rgba(63, 123, 141, 0.1)",
              marginBottom: 16,
              borderColor: Colors.bubbleColor,
            }}
          >
            <Ionicons
              name="information-circle-outline"
              size={14}
              color={Colors.bubbleColor}
            />
            <Text
              style={{
                fontSize: 12,
                color: Colors.bubbleColor,
                fontWeight: "500",
              }}
            >
              {t("calendar-string")}
            </Text>
          </View>
          <Text
            style={{
              fontSize: 16,
              fontWeight: "600",
              marginBottom: 8,
              color: Colors.blackColor,
              textAlign: "center",
            }}
          >
            {t("createTrainingBlockFirst-string")}
          </Text>
          <Text
            style={{
              fontSize: 13,
              color: Colors.blackColor,
              textAlign: "center",
              maxWidth: 500,
              marginBottom: 32,
            }}
          >
            {t("beforeUsingCalendarCreateBlock-string")}
          </Text>
          <TouchableOpacity
            style={{
              backgroundColor: Colors.bubbleColor,
              paddingVertical: 10,
              paddingHorizontal: 20,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: Colors.bubbleColor,
              opacity: isHovered ? 0.6 : 1,
            }}
            onPress={handleGoToProgram}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Text
              style={{
                color: Colors.whiteColor,
                fontSize: 14,
                fontWeight: "500",
              }}
            >
              {t("goToProgram-string")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  });

  /* SCROLL + VISIBILITY (ScrollView) */
  const scrollRef = useRef(null);
  const lastOffsetX = useRef(0);
  const contentWidthRef = useRef(0);
  const containerWidthRef = useRef(0);
  const isAutoScrolling = useRef(false);
  const didInitialScrollThisMount = useRef(false);
  const lastProcessedOffsetX = useRef(0); // Track last X position that triggered recomputeVisibleBlocks

  const setScrollRef = useCallback(
    (ref) => {
      scrollRef.current = ref;
      client.setProperty("scrollRef", ref);
    },
    [client]
  );

  const handleAutoScrollStart = () => {
    isAutoScrolling.current = true;
  };

  const handleAutoScrollEnd = () => {
    isAutoScrolling.current = false;
    lastProcessedOffsetX.current = lastOffsetX.current; // Update processed position
    recomputeVisibleBlocks(); // Update once at the end
  };

  // Create a key from block properties that affect layout to use as a dependency.
  // This ensures positions are recalculated only when necessary.
  const blockStructureKey = (macroBlocks || [])
    .map((b) => `${b.id}:${b.totalWeeks}:${b.showingDashboardActual}`)
    .join("|");

  // Memoize block positions so they are not re-calculated on every scroll event.
  const blockPositions = useMemo(() => {
    let offset = 0;
    const positions = [];
    (macroBlocks || []).forEach((b) => {
      // Align with rendered layout: we use a row gap of 16 between items,
      // so subtract that gap from each block's intrinsic width and add it between items.
      const length = b.getBlockDashboardWidth;

      positions.push({
        id: b.id || b.name,
        start: offset,
        end: offset + length,
      });
      offset += length + 16; // account for row gap
    });
    return positions;
  }, [blockStructureKey]);

  // Helper function to compute week visible range for a specific block
  const computeBlockWeekVisibleRange = useCallback(
    (block, blockStart, blockEnd, viewportStart, viewportEnd) => {
      const totalWeeks = block.totalWeeks || 0;
      const COLUMN_GAP = 40;
      const LEFT_LABEL_WIDTH = 180;
      const INNER_PADDING_H = 16;
      const getColumnWidth = () => (block.showingDashboardActual ? 480 : 264);
      const overscan = 2; // Add some overscan for smooth scrolling

      // Calculate the intersection of block and viewport
      const blockVisibleStart = Math.max(blockStart, viewportStart);
      const blockVisibleEnd = Math.min(blockEnd, viewportEnd);

      // If block is not visible at all, return empty range
      if (blockVisibleStart >= blockVisibleEnd) {
        return { start: 0, end: -1 };
      }

      // Calculate the offset within the block where the viewport starts
      const offsetInBlock = blockVisibleStart - blockStart;

      // Account for the left label and inner padding
      const effectiveOffset = Math.max(
        0,
        offsetInBlock - (LEFT_LABEL_WIDTH + INNER_PADDING_H)
      );

      // Calculate visible viewport width within this block
      const visibleWidth = blockVisibleEnd - blockVisibleStart;

      const colWidth = getColumnWidth();
      const colSpan = colWidth + COLUMN_GAP;

      const start = Math.max(
        0,
        Math.floor(effectiveOffset / colSpan) - overscan
      );
      const end = Math.min(
        totalWeeks - 1,
        Math.ceil((effectiveOffset + visibleWidth) / colSpan) - 1 + overscan
      );
      // console.log("computeBlockWeekVisibleRange", { start, end });
      return { start, end: Math.max(end, start) };
    },
    []
  );

  const recomputeVisibleBlocks = useCallback(() => {
    const viewW = containerWidthRef.current || 0;
    const x = lastOffsetX.current || 0;
    const viewportStart = x;
    const viewportEnd = x + viewW;
    // Be less restrictive: consider items "visible" if they are close to entering the viewport
    // by expanding the viewport with a prefetch margin on both sides.
    // This helps start rendering just before they appear on screen.
    const prefetch = Math.max(200, viewW * 0.5); // 50% of viewport or at least 200px
    const preloadStart = Math.max(0, viewportStart - prefetch);
    const preloadEnd = viewportEnd + prefetch;

    const next = new Set();
    const nextBlockWeekRanges = new Map();

    // Use the memoized positions
    blockPositions.forEach(({ id, start, end }) => {
      // If the block intersects the expanded (preloaded) viewport, treat it as visible.
      const intersectsPreloadedViewport =
        end > preloadStart && start < preloadEnd;
      if (intersectsPreloadedViewport) {
        next.add(id);

        // Compute week visible range for this block
        const block = (macroBlocks || []).find((b) => (b.id || b.name) === id);
        if (block) {
          const weekRange = computeBlockWeekVisibleRange(
            block,
            start,
            end,
            viewportStart,
            viewportEnd
          );
          nextBlockWeekRanges.set(id, weekRange);
        }
      }
    });

    // Prevent re-renders if the set of visible IDs hasn't changed.
    setVisibleIds((currentVisibleIds) => {
      if (
        currentVisibleIds.size === next.size &&
        [...currentVisibleIds].every((id) => next.has(id))
      ) {
        return currentVisibleIds;
      }
      return next;
    });

    // setBlockWeekRanges(nextBlockWeekRanges);
    // Update block week ranges only if they have changed
    setBlockWeekRanges((currentBlockWeekRanges) => {
      // Check if the size is different
      if (currentBlockWeekRanges.size !== nextBlockWeekRanges.size) {
        return nextBlockWeekRanges;
      }

      // Check if any range has changed
      for (const [id, newRange] of nextBlockWeekRanges) {
        const currentRange = currentBlockWeekRanges.get(id);
        if (
          !currentRange ||
          currentRange.start !== newRange.start ||
          currentRange.end !== newRange.end
        ) {
          return nextBlockWeekRanges;
        }
      }

      // No changes detected, return current ranges
      return currentBlockWeekRanges;
    });
  }, [blockPositions, macroBlocks, computeBlockWeekVisibleRange]);

  // Perform the initial one-time scroll to the active block, if we have
  // enough layout information (container and content widths).
  const doInitialScrollIfNeeded = useCallback(() => {
    if (!scrollRef.current) return false;
    if (!isFocused) return false; // only when view appears
    if (didInitialScrollThisMount.current) return false; // already done this mount

    // Always try to restore from stored position
    const stored =
      typeof client?.calendarScrollPosition === "number"
        ? client.calendarScrollPosition
        : null;
    if (stored === null) return false;

    const containerW = containerWidthRef.current || 0;
    const contentW = contentWidthRef.current || 0;
    if (containerW <= 0 || contentW <= 0) return false; // wait for sizes

    const maxOffset = Math.max(0, contentW - containerW);
    const offset = Math.min(Math.max(0, stored), maxOffset);

    // Skip if already at desired offset to avoid redundant scroll
    if (Math.abs((lastOffsetX.current || 0) - offset) < 1) {
      didInitialScrollThisMount.current = true;
      // Ensure visibility state is up to date
      recomputeVisibleBlocks();
      return false;
    }

    handleAutoScrollStart();
    lastOffsetX.current = offset;
    lastProcessedOffsetX.current = offset; // Set processed position for initial scroll
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ x: offset, animated: false });
      handleAutoScrollEnd();
      recomputeVisibleBlocks();
    });
    didInitialScrollThisMount.current = true;
    return true;
  }, [client, isFocused, recomputeVisibleBlocks]);

  // Reset the one-time guard when the view is not focused so it runs again on next appearance
  useEffect(() => {
    if (!isFocused) {
      didInitialScrollThisMount.current = false;
    }
  }, [isFocused]);

  // Persist scroll position on unmount so next render can restore it
  useEffect(() => {
    return () => {
      if (typeof lastOffsetX.current === "number") {
        client.setProperty("calendarScrollPosition", lastOffsetX.current);
      }
    };
  }, [client]);

  // --- Arrow key fixed-step scroll helpers ---
  const scrollRightFixed = () => {
    const scrollStep = 264 + 40;
    const node = scrollRef.current;
    if (!node) return;
    const maxOffset = node.scrollWidth - node.clientWidth;
    const newX = Math.min(lastOffsetX.current + scrollStep, maxOffset);
    lastOffsetX.current = newX;
    node.scrollTo({ x: newX, animated: false });
  };

  const scrollLeftFixed = () => {
    const scrollStep = 264 + 40;
    const node = scrollRef.current;
    if (!node) return;
    const newX = Math.max(lastOffsetX.current - scrollStep, 0);
    lastOffsetX.current = newX;
    node.scrollTo({ x: newX, animated: false });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        if (e.key === "ArrowRight") {
          scrollRightFixed();
        } else {
          scrollLeftFixed();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <View style={{ flex: 1, paddingTop: screenSize === "small" ? 16 : 24 }}>
      {shouldShowProtocolsGuide && (
        <View style={{ marginBottom: 16 }}>
          <ProtocolInfoSection coach={client.coach} animated={true} />
        </View>
      )}
      {macro !== "" && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 12,
            gap: 4,
          }}
        >
          <View
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: client.getLabelColor(macro),
              alignSelf: "center",
            }}
          />
          <Text style={{ fontSize: 18, fontWeight: "bold" }}>{t(macro)}</Text>
        </View>
      )}
      <View
        style={{
          flexDirection: "row",
          gap: 16,
          justifyContent: "center",
        }}
      >
        <View
          style={{
            maxWidth: "100%",
            flexShrink: 1,
          }}
        >
          {!isMobile && (
            <>
              <ScrollButtons
                scrollRef={scrollRef}
                lastOffsetX={lastOffsetX}
                scrollDirection="right"
                marginTop={24}
                onScrollStart={handleAutoScrollStart}
                onScrollEnd={handleAutoScrollEnd}
              />
              <ScrollButtons
                scrollRef={scrollRef}
                lastOffsetX={lastOffsetX}
                scrollDirection="left"
                marginTop={24}
                onScrollStart={handleAutoScrollStart}
                onScrollEnd={handleAutoScrollEnd}
              />
            </>
          )}
          {macroBlocks?.length > 0 && (
            <ScrollView
              ref={setScrollRef}
              horizontal
              style={{ maxWidth: "100%", flexShrink: 1 }}
              showsHorizontalScrollIndicator={false}
              onScroll={(e) => {
                const newOffsetX = e.nativeEvent.contentOffset.x;
                lastOffsetX.current = newOffsetX;

                // Only recompute if X position has changed significantly and not auto-scrolling
                if (!isAutoScrolling.current) {
                  const xDifference = Math.abs(
                    newOffsetX - lastProcessedOffsetX.current
                  );
                  if (xDifference > 5) {
                    // Only trigger if X moved more than 5px
                    lastProcessedOffsetX.current = newOffsetX;
                    recomputeVisibleBlocks();
                  }
                }
              }}
              scrollEventThrottle={100}
              onContentSizeChange={(w, h) => {
                contentWidthRef.current = w;
                // Try to perform the one-time initial scroll once we know sizes
                const didInit = doInitialScrollIfNeeded();
                if (!didInit) {
                  // Update processed X position since content size changed
                  lastProcessedOffsetX.current = lastOffsetX.current;
                  recomputeVisibleBlocks();
                }
              }}
              onLayout={(e) => {
                containerWidthRef.current = e.nativeEvent.layout.width;
                // Try to perform the one-time initial scroll once we know sizes
                const didInit = doInitialScrollIfNeeded();
                if (!didInit) {
                  // Update processed X position since layout changed
                  lastProcessedOffsetX.current = lastOffsetX.current;
                  recomputeVisibleBlocks();
                  // console.log("onLayout", containerWidthRef);
                }
              }}
            >
              <View style={{ flexDirection: "row", gap: 16 }}>
                {(macroBlocks || []).map((block) => {
                  const id = block.id || block.name;
                  const widthForLayout = block.getBlockDashboardWidth; // keep consistent with positions/gap
                  const isVisible = visibleIds.has(id);
                  const weekVisibleRange =
                    blockWeekRanges.get(id) || defaultWeekRange;

                  return (
                    <View key={id}>
                      {isVisible ? (
                        <>
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 14,
                                fontWeight: "400",
                              }}
                            >
                              {block.name}
                            </Text>
                            <OpenBlockButton
                              block={block}
                              isMobile={isMobile}
                            />
                          </View>

                          <FullBlockView
                            block={block}
                            calendar={true}
                            useScrollContainer={false}
                            weekVisibleRange={weekVisibleRange}
                          />
                        </>
                      ) : (
                        <PulseSkeleton
                          style={{
                            width: widthForLayout,
                            borderRadius: 14,
                            marginTop: 24,
                            borderRadius: 14,
                            borderWidth: 0.1,
                            borderColor: Colors.borderColor,
                            minHeight: 500,
                          }}
                        />
                      )}
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          )}
        </View>

        {screenSize != "small" && focusedBlock?.showingInfoView && (
          <View style={{ marginTop: 24 }}>
            <InfoView
              client={client}
              block={focusedBlock}
              exercise={focusedBlock.selectedSlot}
              calendar={true}
              coach={null}
            />
          </View>
        )}
        {screenSize === "small" && focusedBlock?.showingInfoView && (
          <PopupInfoView
            block={focusedBlock}
            isVisible={focusedBlock}
            onClose={() => focusedBlock.setProperty("showingInfoView", false)}
          />
        )}
        {macroBlocks?.length === 0 && !focusedBlock?.showingInfoView && (
          <View
            style={{
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconEmptyView
              iconLibrary={"MaterialCommunityIcons"}
              mainText={t("emptyBlocksInMacroTitle-string")}
              subText={t("emptyBlocksInMacroText-string")}
              iconName={"cursor-default-click-outline"}
              marginTop={40}
              marginBottom={8}
            />
          </View>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    padding: 8,
  },
});

export default MacroDescriptionView;
